// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { Block, Rule } from '../types';

export const insertAt = <T>(items: T[], at: number, ...inserted: T[]): T[] => [
  ...items.slice(0, at),
  ...inserted,
  ...items.slice(at),
];

// The one splice point for both encode and insertion: the first OTHERWISE, or the end.
export const beforeOtherwise = (rules: Rule[]): number => {
  const found = rules.findIndex(rule => rule.kind === 'otherwise');
  return found === -1 ? rules.length : found;
};

export const insertTenantRule = (rules: Rule[], rule: Rule): Rule[] =>
  insertAt(rules, beforeOtherwise(rules), rule);

export const toBlocks = (rules: Rule[]): Block[] => {
  const blocks: Block[] = [];
  for (const rule of rules) {
    const open = blocks.at(-1);
    if (rule.kind === 'system') { blocks.push({ kind: 'systemRuleBlock', rule }); }
    else if (rule.kind === 'otherwise') { blocks.push({ kind: 'otherwiseBlock', rule }); }
    else if (open?.kind === 'tenantRuleBlock') { open.rules.push(rule); }
    else { blocks.push({ kind: 'tenantRuleBlock', rules: [rule] }); }
  }
  return blocks;
};

export const flattenBlocks = (blocks: Block[]): Rule[] =>
  blocks.flatMap<Rule>(block => (block.kind === 'tenantRuleBlock' ? block.rules : [block.rule]));

export const reorder = <T>(items: T[], from: number, to: number): T[] | null => {
  const moved = items[from];
  if (moved === undefined || to < 0 || to >= items.length || from === to) { return null; }
  return insertAt(
    items.filter((_, index) => index !== from),
    to,
    moved,
  );
};
